import React, { useState } from 'react';
import { companyInfo, roofTypes } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Phone, Mail, MapPin, Send, CheckCircle, Zap, ArrowRight, Shield, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consumption: '',
    roofType: '',
    message: '',
    honeypot: '' // Spam protection - should remain empty
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone) => {
    const regex = /^[\d\s+\-()]{6,20}$/;
    return regex.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Bitte geben Sie Ihren Namen ein';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Bitte geben Sie Ihre E-Mail-Adresse ein';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Bitte geben Sie Ihre Telefonnummer ein';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Bitte geben Sie eine gültige Telefonnummer ein';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, roofType: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot spam check - if filled, silently reject
    if (formData.honeypot) {
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 3000);
      return;
    }
    
    if (!validateForm()) {
      toast.error('Bitte füllen Sie alle Pflichtfelder korrekt aus.');
      return;
    }
    
    setIsSubmitting(true);

    // MOCK: Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen.');
      setFormData({ name: '', email: '', phone: '', consumption: '', roofType: '', message: '', honeypot: '' });
      setErrors({});
    }, 1500);
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-sun-orange/10 text-sun-orange rounded-full text-sm font-semibold mb-4">
            Jetzt starten
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Starten Sie jetzt Ihr eigenes
            <span className="text-sun-orange"> Energieprojekt</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Wir analysieren Ihr Dach, berechnen die Wirtschaftlichkeit und planen 
            Ihr individuelles Photovoltaiksystem.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
              <CardContent className="p-8 lg:p-10">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-14 h-14 bg-sun-orange rounded-2xl flex items-center justify-center">
                    <Zap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Kostenlose Beratung anfragen</h3>
                    <p className="text-slate-500">Wir melden uns innerhalb von 24 Stunden</p>
                  </div>
                </div>

                {/* Success Message */}
                {isSubmitted && (
                  <div data-testid="contact-success-message" className="mb-8 p-6 bg-green-50 border border-green-200 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-green-800 text-lg">Anfrage erfolgreich gesendet!</h4>
                        <p className="text-green-700">Vielen Dank für Ihr Interesse. Wir melden uns innerhalb von 24 Stunden bei Ihnen.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  {/* Honeypot field - hidden from users, bots fill it */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-base font-medium">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Ihr vollständiger Name"
                        data-testid="contact-name-input"
                        className={`h-14 text-base border-slate-200 focus:border-sun-orange focus:ring-sun-orange ${errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> {errors.name}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-base font-medium">Telefon *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Für Rückruf"
                        data-testid="contact-phone-input"
                        className={`h-14 text-base border-slate-200 focus:border-sun-orange focus:ring-sun-orange ${errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base font-medium">E-Mail *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Ihre E-Mail-Adresse"
                      data-testid="contact-email-input"
                      className={`h-14 text-base border-slate-200 focus:border-sun-orange focus:ring-sun-orange ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {errors.email}
                      </p>
                    )}
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="consumption" className="text-base font-medium">Stromverbrauch pro Jahr</Label>
                      <div className="relative">
                        <Input
                          id="consumption"
                          name="consumption"
                          type="number"
                          value={formData.consumption}
                          onChange={handleChange}
                          placeholder="z.B. 4500"
                          className="h-14 text-base pr-16 border-slate-200 focus:border-sun-orange focus:ring-sun-orange"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">kWh</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base font-medium">Dachform</Label>
                      <Select onValueChange={handleSelectChange} value={formData.roofType}>
                        <SelectTrigger className="h-14 text-base border-slate-200 focus:border-sun-orange focus:ring-sun-orange">
                          <SelectValue placeholder="Dachform wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {roofTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-base font-medium">Ihre Nachricht</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Beschreiben Sie kurz Ihr Vorhaben oder stellen Sie Ihre Fragen..."
                      rows={4}
                      className="text-base border-slate-200 focus:border-sun-orange focus:ring-sun-orange resize-none"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting || isSubmitted}
                    data-testid="contact-submit-button"
                    className="w-full h-16 bg-sun-orange hover:bg-sun-orange-dark text-white text-lg font-semibold shadow-xl shadow-sun-orange/30 transition-all hover:shadow-sun-orange/40 hover:scale-[1.01] rounded-xl disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Wird gesendet...
                      </span>
                    ) : isSubmitted ? (
                      <span className="flex items-center justify-center">
                        <CheckCircle className="mr-2 h-6 w-6" />
                        Erfolgreich gesendet!
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        Kostenlose Beratung anfragen
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </span>
                    )}
                  </Button>

                  {/* Trust Badge */}
                  <div className="flex items-center justify-center gap-2 pt-4 text-slate-500">
                    <Shield className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Kostenlose und unverbindliche Erstberatung</span>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl p-8 text-white">
              <h3 className="text-xl font-bold mb-8">Direkter Kontakt</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sun-orange rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{companyInfo.contact.person}</p>
                    <p className="text-white/60 text-sm mb-1">{companyInfo.contact.position}</p>
                    <a href={`tel:${companyInfo.contact.phone}`} className="text-sun-orange hover:underline font-medium">
                      {companyInfo.contact.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">E-Mail</p>
                    <a href={`mailto:${companyInfo.contact.email}`} className="text-white/80 hover:text-sun-orange transition-colors">
                      {companyInfo.contact.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{companyInfo.contact.address.label}</p>
                    <p className="text-white/70 text-sm">
                      {companyInfo.contact.address.street}<br />
                      {companyInfo.contact.address.zip} {companyInfo.contact.address.city}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Trust Box */}
            <div className="bg-gradient-to-br from-sun-orange/10 to-amber-100/50 rounded-2xl p-6 border border-sun-orange/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-sun-orange rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2">100% kostenlos & unverbindlich</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Die Erstberatung und das Angebot sind für Sie vollkommen kostenlos. 
                    Entscheiden Sie in Ruhe, ohne jede Verpflichtung.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
